import React, { ReactNode, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { User, LogOut } from 'lucide-react';
import { debug } from '@/lib/utils';
import AddToHomeScreenPrompt from './shared/AddToHomeScreenPrompt';
import { TabBar, TabId, TabBarActionButton } from './ui/tab-bar';

type TradesPurpleLayoutProps = {
  children: ReactNode;
  activeTab?: TabId;
  onTabChange?: (tab: TabId) => void;
  showTabBar?: boolean;
  actionButton?: TabBarActionButton;
};

// Get a random but consistent image for the day
function getImageForToday() {
  const today = new Date();
  const dateKey = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
  // Simple hash of the date to randomly but consistently select an image
  return dateKey % 2 === 0
    ? "/tradespurple/tradespurple-illustration.png"
    : "/tradespurple/TPman.png";
}

// Type for navigator.standalone in iOS Safari
interface SafariNavigator extends Navigator {
  standalone?: boolean;
}

function TradesPurpleLayout({
  children,
  activeTab = 'list',
  onTabChange,
  showTabBar = false,
  actionButton
}: TradesPurpleLayoutProps) {
  const { isAuthenticated, user, signOut } = useAuth();
  const location = useLocation();
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  // Check if user is on their account page (LoggedInLandingPage)
  const isAccountPage = isAuthenticated && location.pathname === '/';

  // Get image path based on today's date (will be stable for the whole day)
  const imageSrc = getImageForToday();
  const imageAlt = imageSrc.includes("TPman")
    ? "TradesPurple illustration showing a man using the service on his phone"
    : "TradesPurple illustration showing a woman using the service on her phone";

  // Detect PWA standalone mode and iOS
  useEffect(() => {
    // Check if running in standalone mode (installed PWA)
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                       (window.navigator as SafariNavigator).standalone === true;
    setIsStandalone(standalone);

    // Check if on iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Add iOS standalone class to the document if in PWA mode on iOS
    if (standalone && iOS) {
      document.documentElement.classList.add('ios-standalone');
    }
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
      // No need to navigate, AuthContext will handle that
    } catch (error) {
      debug.error('Logout failed:', error);
    }
  };

  return (
    <div className={`w-full md:max-w-[600px] mx-auto flex flex-col min-h-[100vh] ${isAuthenticated ? 'bg-white' : ''}`} style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Top header with login/logout link - hide on account page */}
      {!isAccountPage && (
        <div className={`absolute top-3 right-3 md:right-6 ${isStandalone && isIOS ? 'pt-safe' : ''}`}>
          {isAuthenticated ? (
            <Link
              to="/"
              className="flex items-center text-purple-800 hover:text-purple-900"
            >
              <User className="h-5 w-5 mr-1" />
              <span className="text-sm">My Page</span>
            </Link>
          ) : (
            <Link
              to="/auth/login"
              className="flex items-center text-purple-800 hover:text-purple-900"
            >
              <User className="h-5 w-5 mr-1" />
              <span className="text-sm">Log in</span>
            </Link>
          )}
        </div>
      )}

      {/* TradesPurple hero section - hidden on mobile when tab bar is shown */}
      {!showTabBar && (
        <>
          <a href="https://tradespurple.com" className={`flex justify-center mb-1 ${isStandalone && isIOS ? 'pt-safe' : ''}`}>
            <img
              src={imageSrc}
              alt={imageAlt}
              className="h-64 max-w-full" /* Larger size as requested */
            />
          </a>

          {/* TradesPurple title */}
          <h1 className="text-3xl font-bold text-center mt-1 mb-1" style={{ color: "#5D4A9C" }}>
            TradesPurple
          </h1>
          {/* Only show subtitle on non-account pages */}
          {!isAccountPage && (
            <p className="text-center text-base md:text-lg text-purple-700 mt-1 mb-4 font-medium">
              Find the tradespeople<br />
              your neighbours trust.
            </p>
          )}
        </>
      )}

      {/* Main Content - fills available space */}
      <div className={`flex-1 flex flex-col`} style={showTabBar ? { paddingBottom: 'calc(83px + env(safe-area-inset-bottom, 0px))' } : {}}>
        {children}
      </div>

      {/* Footer - only show on public front page (logged out users) */}
      {!isStandalone && !isAuthenticated && (
        <footer className="mt-8 pt-4 border-t-2 border-purple-200 text-center text-gray-600 pb-safe safe-inset-bottom">
        <div className="space-y-2">
          <div className="flex items-center justify-center gap-4 text-sm">
            <a href="/legal#privacy" className="hover:text-purple-700 transition-colors">Privacy</a>
            <span className="text-purple-300">•</span>
            <a href="/legal#terms" className="hover:text-purple-700 transition-colors">Terms</a>
            <span className="text-purple-300">•</span>
            <a href="mailto:maxtattonbrown+tradespurple@gmail.com" className="hover:text-purple-700 transition-colors">Contact</a>
          </div>


          <p className="text-xs text-gray-500">
            © {new Date().getFullYear()} TradesPurple. All rights reserved.
          </p>
        </div>
        </footer>
      )}

      {/* PWA installation prompt - shows only when appropriate */}
      {!isStandalone && <AddToHomeScreenPrompt />}

      {/* Mobile Tab Bar */}
      {showTabBar && onTabChange && (
        <TabBar
          activeTab={activeTab}
          onTabChange={onTabChange}
          actionButton={actionButton}
        />
      )}
    </div>
  );
}

// Single clear export
export default TradesPurpleLayout;
